import {
  AppBar as MuiAppBar,
  Container,
  Grid,
  makeStyles,
} from "@material-ui/core";
import React from "react";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import { createStyles, Theme } from "@material-ui/core/styles";
import { Link } from "react-router-dom";

// from https://github.com/mui-org/material-ui/blob/master/docs/src/pages/components/app-bar/SearchAppBar.tsx
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    appBar: {
      top: theme.layoutDimensions.mainAppBarHeight,
      [theme.breakpoints.up("lg")]: {
        paddingLeft: theme.layoutDimensions.mainDrawerWidth,
        "& .MuiToolbar-gutters": {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
      minHeight: theme.layoutDimensions.secondaryAppBarHeight,
      boxShadow: "none",
    },
    leftContent: {
      flexGrow: 1,
      display: "block",
      [theme.breakpoints.up("lg")]: {
        display: "block",
      },
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    breadcrumb: {
      color: theme.palette.secondary.contrastText,
      textDecoration: "none",
    },
    rightContent: {
      display: "inline-flex",
      float: "right",
      color: theme.palette.secondary.contrastText,
      [theme.breakpoints.down("lg")]: {
        right: "0px",
        // position: "absolute",
      },
      backgroundColor: "#293445",
      paddingLeft: 20,
      paddingTop: 2,
      paddingBottom: 2,
    },
    rightButton: {},
    container: {
      padding: 0,
      margin: theme.layoutDimensions.mainDrawerWidth === 0 ? undefined : 0,
    },
  })
);

export type AppBarProps = {
  breadcrumbs?: { label: string; to?: string; dataTest?: string }[];
  moduleName: string;
};

export const AppBar: React.FC<AppBarProps> = ({
  breadcrumbs = [],
  moduleName,
  children,
}) => {
  const classes = useStyles();

  const buildLink = (breadcrumb: typeof breadcrumbs[0]) => {
    const { to, label, dataTest } = breadcrumb;
    if (to === null || to === undefined) {
      return <span>{label}</span>;
    }

    // We need to be careful to not add a slash to the end of urls for root pages where
    //  they do not belong e.g. we want breadcrumbs to navigate to ".../users"
    //  not ".../users/".  So we need to examine the "to" parameter of each breadcrumb
    //  to asses if it is pointing back to the root.  This means any urls that are empty
    //  or begin with ? should not be delimited by a slash.
    const isLinkToRoot = !to || to[0] === "?";
    const noTrailingSlashTo = `/${moduleName}${isLinkToRoot ? "" : "/"}${to}`;

    return (
      <Link
        to={noTrailingSlashTo}
        className={classes.breadcrumb}
        data-test={dataTest}
      >
        {label}
      </Link>
    );
  };

  return (
    <div className={classes.root}>
      <MuiAppBar
        position="fixed"
        className={classes.appBar}
        color="secondary"
        data-test="secondary-app-bar"
      >
        <Container maxWidth="xl" className={classes.container}>
          <Toolbar>
            <Grid container alignItems="center">
              <Grid item md={7}>
                <Typography
                  className={classes.leftContent}
                  variant="h1"
                  data-test="secondary-app-bar-title"
                >
                  {breadcrumbs
                    .filter((breadcrumb) => !!breadcrumb)
                    .map((breadcrumb, index) => (
                      <React.Fragment key={index}>
                        {index ? (
                          <>&nbsp;/&nbsp;</>
                        ) : // first item doesn't get a delimiter
                        null}
                        {buildLink(breadcrumb)}
                      </React.Fragment>
                    ))}
                </Typography>
              </Grid>
              <Grid item md={5}>
                <span
                  className={`secondaryToolbarButtons ${classes.rightContent}`}
                >
                  {children}
                </span>
              </Grid>
            </Grid>
          </Toolbar>
        </Container>
      </MuiAppBar>
    </div>
  );
};
